import { Beneficiary } from '../models/beneficiary';
import { BeneficiaryShareChangeTypeEnum } from '../../shared/components/beneficiary-submission-share-change/beneficiary-share-change-type.enum';
import { BeneficiaryShareChanges } from '../../shared/components/beneficiary-submission-share-change/beneficiary-share-change-type';
import { BeneficiaryEntityTypeEnum } from '../enums/beneficiary-entity-type.enum';
import { BeneficiaryRelationshipTypeEnum } from '../enums/beneficiary-relationship-type-enum';
import { SubmissionInfoItem } from '../../../models/shared/submission-info-item';
import { BeneficiaryDetailsChanges } from '../models/beneficiary-details-changes';
import * as moment from 'moment';
import { PhoneNumberUtils } from '../../../utils/phone-number-utils';
import { EstatePrimaryContactTypeEnum } from '../enums/estate-primary-contact-type.enum';
import { AccountTypeBeneficiaryFormGroup } from '../forms/account-type-beneficiary-form-group';
import { Address } from '../../../models/users/dto/address';
import { BeneficiaryTrustee } from '../models/beneficiary-trustee';
import { BeneficiaryTypeEnum } from '../enums/beneficiary-type.enum';

export class BeneficiarySubmissionUtils {
  /**
   * Formats a SubmissionInfoItem for display, including localization and specific formatting.
   * @param change - The SubmissionInfoItem object to format.
   * @return A formatted SubmissionInfoItem with localized key and formatted value.
   */
  public static formatBeneficiaryDetailsChanges(change: SubmissionInfoItem): SubmissionInfoItem {
    let label = '';
    let value = change.value;

    switch (change.label) {
      case 'firstName':
        label = $localize`First Name`;
        break;
      case 'lastName':
        label = $localize`Last Name`;
        break;
      case 'organizationName':
        label = $localize`Organization Name`;
        break;
      case 'organizationBusinessNumber':
        label = $localize`Business Number / Registered Charity Number`;
        break;
      case 'relationshipToMember':
        label = $localize`Relationship to Member`;
        break;
      case 'birthDate':
        label = $localize`Date of Birth`;
        if (!!value) {
          value = moment(value).format('MMM D, YYYY');
        }
        break;
      case 'socialInsuranceNumber':
        label = $localize`Social Insurance Number`;
        break;
      case 'email':
        label = $localize`Email Address`;
        break;
      case 'phoneNumber':
        label = $localize`Phone Number`;
        value = PhoneNumberUtils.formatPhoneNumber(value.toString());
        break;
      case 'address.addressLine1':
        label = $localize`Address Line 1`;
        break;
      case 'address.addressLine2':
        label = $localize`Address Line 2`;
        break;
      case 'address.addressLine3':
        label = $localize`Address Line 3`;
        break;
      case 'address.city':
        label = $localize`City/Town`;
        break;
      case 'address.country':
        label = $localize`Country`;
        break;
      case 'address.state':
        label = $localize`Province/State/Region`;
        break;
      case 'address.postalCode':
        label = $localize`Postal Code/ZIP`;
        break;
    }

    return { key: change.key, label, value, hasChanged: change.hasChanged };
  }

  private static getKeysToCompare(entityType: BeneficiaryEntityTypeEnum): string[] {
    switch (entityType) {
      case BeneficiaryEntityTypeEnum.Individual:
        return [
          'relationshipToMember',
          'birthDate',
          'socialInsuranceNumber',
          'email',
          'phoneNumber',
          'address.country',
          'address.addressLine1',
          'address.addressLine2',
          'address.addressLine3',
          'address.city',
          'address.state',
          'address.postalCode'
        ];
      case BeneficiaryEntityTypeEnum.Estate:
        return [
          'firstName',
          'lastName',
          'phoneNumber',
          'organizationName',
          'address.country',
          'address.addressLine1',
          'address.addressLine2',
          'address.addressLine3',
          'address.city',
          'address.state',
          'address.postalCode'
        ];
      case BeneficiaryEntityTypeEnum.Organization:
        return [
          'firstName',
          'lastName',
          'organizationBusinessNumber',
          'organizationName',
          'phoneNumber',
          'address.country',
          'address.addressLine1',
          'address.addressLine2',
          'address.addressLine3',
          'address.city',
          'address.state',
          'address.postalCode'
        ];
      default:
        return [];
    }
  }

  /*
   * Compares two lists of beneficiaries and marks them as changed if any of the specified fields are different.
   * @param prevBeneficiaries - An array of previous Beneficiary objects to compare against.
   * @param newBeneficiaries - An array of new Beneficiary objects to compare.
   * @returns - A formatted array of BeneficiaryDetailsChanges indicating the changes for each beneficiary.
   */
  public static getBeneficiaryDetailsChanges(
    prevBeneficiaries: Beneficiary[],
    newBeneficiaries: Beneficiary[],
    memberFullName: string
  ): BeneficiaryDetailsChanges[] {
    const result = [];
    let fullName = '';

    for (const newB of newBeneficiaries) {
      const match = prevBeneficiaries.find(
        prev =>
          prev.memberBeneficiaryId !== null &&
          newB.memberBeneficiaryId !== null &&
          prev.memberBeneficiaryId === newB.memberBeneficiaryId
      );

      const previousChanges: SubmissionInfoItem[] = [];
      const newChanges: SubmissionInfoItem[] = [];
      // Properties to compare
      const keys = this.getKeysToCompare(newB.entityTypeId);

      for (const k of keys) {
        const key = k as keyof Beneficiary;
        let hasChanged = false;
        let prevVal: any = '';
        let newVal: any = '';
        let displayPrev: string = '';
        let displayNew: string = '';

        if (match) {
          // Special handling for nested objects and specific formatting
          // i.e: We want to compare country.id but display country.name
          switch (k) {
            case 'relationshipToMember':
              prevVal = match.relationshipToMember?.id;
              newVal = newB.relationshipToMember?.id;
              displayPrev = match.relationshipToMember?.name ?? '';
              displayNew = newB.relationshipToMember?.name ?? '';
              hasChanged = prevVal !== newVal;
              break;

            case 'address.country':
              prevVal = match.address?.country?.id;
              newVal = newB.address?.country?.id;
              displayPrev = match.address?.country?.name ?? '';
              displayNew = newB.address?.country?.name ?? '';
              hasChanged = prevVal !== newVal;
              break;

            case 'address.state':
              prevVal = match.address?.state?.id;
              newVal = newB.address?.state?.id;
              displayPrev = match.address?.state?.name ?? '';
              displayNew = newB.address?.state?.name ?? '';
              hasChanged = prevVal !== newVal;
              break;
            case 'address.postalCode':
              prevVal = match.address?.postalCode ?? '';
              newVal = newB.address?.postalCode ?? '';
              displayNew = newVal;
              displayPrev = prevVal;
              hasChanged = prevVal !== newVal;
              break;
            case 'address.city':
              prevVal = match.address?.city ?? '';
              newVal = newB.address?.city ?? '';
              displayNew = newVal;
              displayPrev = prevVal;
              hasChanged = prevVal !== newVal;
              break;
            case 'address.addressLine1':
              prevVal = match.address?.addressLine1 ?? '';
              newVal = newB.address?.addressLine1 ?? '';
              displayNew = newVal;
              displayPrev = prevVal;
              hasChanged = prevVal !== newVal;
              break;
            case 'address.addressLine2':
              prevVal = match.address?.addressLine2 ?? '';
              newVal = newB.address?.addressLine2 ?? '';
              displayNew = newVal;
              displayPrev = prevVal;
              hasChanged = prevVal !== newVal;
              break;
            case 'address.addressLine3':
              prevVal = match.address?.addressLine3 ?? '';
              newVal = newB.address?.addressLine3 ?? '';
              displayNew = newVal;
              displayPrev = prevVal;
              hasChanged = prevVal !== newVal;
              break;
            default:
              prevVal = match[key];
              newVal = newB[key];
              hasChanged = prevVal !== newVal;
              displayPrev = prevVal ?? '';
              displayNew = newVal ?? '';
              break;
          }

          previousChanges.push({ key: k, label: k, value: displayPrev, hasChanged });
          newChanges.push({ key: k, label: k, value: displayNew, hasChanged });
        } else {
          // No previous data: treat all as new with hasChanged = true
          switch (k) {
            case 'relationshipToMember':
              newVal = newB.relationshipToMember?.name ?? '';
              break;
            case 'address.addressLine1':
              newVal = newB.address?.addressLine1 ?? '';
              break;
            case 'address.addressLine2':
              newVal = newB.address?.addressLine2 ?? '';
              break;
            case 'address.addressLine3':
              newVal = newB.address?.addressLine3 ?? '';
              break;
            case 'address.country':
              newVal = newB.address?.country?.name ?? '';
              break;
            case 'address.state':
              newVal = newB.address?.state?.name ?? '';
              break;
            case 'address.postalCode':
              newVal = newB.address?.postalCode ?? '';
              break;
            case 'address.city':
              newVal = newB.address?.city ?? '';
              break;
            default:
              newVal = newB[key] ?? '';
              break;
          }

          newChanges.push({ key: k, label: k, value: newVal, hasChanged: true });
        }
      }
      // Assign name based on entity type
      if (newB.entityTypeId === BeneficiaryEntityTypeEnum.Individual) {
        fullName = [newB.firstName, newB.middleName, newB.lastName].filter(name => !!name).join(' ');
      } else if (newB.entityTypeId === BeneficiaryEntityTypeEnum.Estate) {
        fullName = $localize`Estate of ${memberFullName}`;
      } else {
        fullName = newB.organizationName ?? '';
      }

      result.push({
        fullName,
        previous: previousChanges,
        new: newChanges,
        entityType: newB.entityTypeId
      });
    }

    return result;
  }

  private static beneficiaryCardSortFn(a: Beneficiary, b: Beneficiary): number {
    const getSortPriority = (beneficiary: Beneficiary): number => {
      if (beneficiary.relationshipToMemberId === BeneficiaryRelationshipTypeEnum.Spouse) return 0;
      if (beneficiary.entityTypeId === BeneficiaryEntityTypeEnum.Individual) return 1;
      if (beneficiary.entityTypeId === BeneficiaryEntityTypeEnum.Organization) return 2;
      if (beneficiary.entityTypeId === BeneficiaryEntityTypeEnum.Estate) return 3;
      return 4;
    };

    const getSortKey = (beneficiary: Beneficiary): string => {
      if (beneficiary.entityTypeId === BeneficiaryEntityTypeEnum.Individual) {
        return [beneficiary.firstName, beneficiary.middleName, beneficiary.lastName]
          .filter(name => !!name)
          .join(' ')
          .toLowerCase();
      } else {
        return (beneficiary.organizationName ?? '').toLowerCase();
      }
    };

    const priorityA = getSortPriority(a);
    const priorityB = getSortPriority(b);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    const keyA = getSortKey(a);
    const keyB = getSortKey(b);

    return keyA.localeCompare(keyB);
  }

  public static compareBeneficiaryShares(
    previousBeneficiaries: Beneficiary[],
    newBeneficiaries: Beneficiary[],
    memberName: string,
    shareKey: keyof Beneficiary
  ): BeneficiaryShareChanges {
    const oldMap = new Map<number, Beneficiary>();
    const newMap = new Map<number, Beneficiary>();

    const sortedPrevious = previousBeneficiaries.sort(this.beneficiaryCardSortFn);
    const sortedNew = newBeneficiaries.sort(this.beneficiaryCardSortFn);

    for (const oldB of sortedPrevious) {
      if (oldB.memberBeneficiaryId !== null) {
        oldMap.set(oldB.memberBeneficiaryId, oldB);
      }
    }

    for (const newB of sortedNew) {
      if (newB.memberBeneficiaryId !== null) {
        newMap.set(newB.memberBeneficiaryId, newB);
      }
    }

    const result: BeneficiaryShareChanges = {
      old: [],
      new: []
    };

    for (const newB of sortedNew) {
      const id = newB.memberBeneficiaryId;

      const name = this.getBeneficiaryName(newB, memberName);

      // If memberBeneficiaryId is missing, treat as brand new
      if (!id) {
        result.new.push({
          name,
          share: this.getBeneficiaryShare(newB, shareKey),
          changeType: BeneficiaryShareChangeTypeEnum.New
        });
        continue;
      }

      const oldB = oldMap.get(id);

      if (!oldB) {
        // ID exists but wasn't in old list, treat as new
        result.new.push({
          name,
          share: this.getBeneficiaryShare(newB, shareKey),
          changeType: BeneficiaryShareChangeTypeEnum.New
        });
      } else if (this.getBeneficiaryShare(oldB, shareKey) !== this.getBeneficiaryShare(newB, shareKey)) {
        // Share changed
        result.old.push({
          name: this.getBeneficiaryName(oldB, memberName),
          share: this.getBeneficiaryShare(oldB, shareKey),
          changeType: BeneficiaryShareChangeTypeEnum.Previous
        });

        result.new.push({
          name,
          share: this.getBeneficiaryShare(newB, shareKey),
          changeType: BeneficiaryShareChangeTypeEnum.New
        });
      } else {
        // No changes
        result.old.push({
          name,
          share: this.getBeneficiaryShare(oldB, shareKey),
          changeType: BeneficiaryShareChangeTypeEnum.Neutral
        });

        result.new.push({
          name,
          share: this.getBeneficiaryShare(newB, shareKey),
          changeType: BeneficiaryShareChangeTypeEnum.Neutral
        });
      }
    }

    for (const oldB of sortedPrevious) {
      const id = oldB.memberBeneficiaryId;
      if (!!id && !newMap.has(id)) {
        result.old.push({
          name: this.getBeneficiaryName(oldB, memberName),
          share: this.getBeneficiaryShare(oldB, shareKey),
          changeType: BeneficiaryShareChangeTypeEnum.Removed
        });
      }
    }

    return result;
  }

  private static getBeneficiaryShare(beneficiary: Beneficiary, shareKey: keyof Beneficiary): number {
    if (shareKey === 'alternateSharePercentage') {
      return beneficiary.alternateSharePercentage || beneficiary.sharePercentage || 0;
    }

    return beneficiary[shareKey] as number;
  }

  private static getBeneficiaryName(b: Beneficiary, memberName: string): string {
    switch (b.entityTypeId) {
      case BeneficiaryEntityTypeEnum.Individual:
        return [b.firstName, b.middleName, b.lastName].filter(n => n).join(' ');
      case BeneficiaryEntityTypeEnum.Organization:
        return b.organizationName ?? '';
      case BeneficiaryEntityTypeEnum.Estate:
        return $localize`Estate of ${memberName}`;
      default:
        return '';
    }
  }

  private static constructIndividualBeneficiary(formGroup: AccountTypeBeneficiaryFormGroup): Beneficiary {
    const beneficiary = new Beneficiary();
    const Deserialize = window?.injector.Deserialize;
    const personalInfoGroup = formGroup.individual?.controls?.personalInfo;
    const contactInfoGroup = formGroup.individual?.controls?.contactInfo;

    beneficiary.memberBeneficiaryId = formGroup.memberBeneficiaryId.value ?? null;
    beneficiary.linkedSpouseId = formGroup.linkedSpouseId.value ?? null;
    beneficiary.typeId = formGroup.typeId.value;
    beneficiary.entityTypeId = formGroup.entityTypeId.value;
    beneficiary.relationshipToMemberId = personalInfoGroup?.controls?.relationshipToMemberId?.value ?? 0;
    beneficiary.relationshipToMemberOtherDescription =
      personalInfoGroup?.controls?.relationshipToMemberOtherDescription?.value ?? null;
    beneficiary.firstName = personalInfoGroup?.controls?.firstName?.value ?? null;
    beneficiary.middleName = personalInfoGroup?.controls?.middleName?.value ?? null;
    beneficiary.lastName = personalInfoGroup?.controls?.lastName?.value ?? null;
    beneficiary.socialInsuranceNumber = personalInfoGroup?.controls?.socialInsuranceNumber?.value ?? '';
    beneficiary.email = contactInfoGroup?.controls?.email?.value ?? '';
    beneficiary.phoneNumberCountryCode = contactInfoGroup?.controls?.phoneNumberCountryCode?.value ?? '';
    beneficiary.phoneNumber = contactInfoGroup?.controls?.phoneNumber?.value ?? '';
    beneficiary.birthDate = personalInfoGroup?.controls?.birthDate?.value ?? '';
    beneficiary.address = Deserialize?.instanceOf(Address, contactInfoGroup?.controls?.address?.value) ?? null;
    if (formGroup.individual?.controls?.hasTrustee?.value) {
      beneficiary.trustee =
        Deserialize?.instanceOf(BeneficiaryTrustee, formGroup?.individual?.controls?.trustee?.value) ?? null;
    }

    return beneficiary;
  }

  private static constructOrganizationBeneficiary(formGroup: AccountTypeBeneficiaryFormGroup): Beneficiary {
    const beneficiary = new Beneficiary();
    const Deserialize = window?.injector.Deserialize;
    const organizationControls = formGroup.organization?.controls;
    const primaryContactControls = organizationControls?.primaryContact?.controls;
    beneficiary.memberBeneficiaryId = formGroup.memberBeneficiaryId.value ?? null;
    beneficiary.typeId = formGroup.typeId.value;
    beneficiary.entityTypeId = formGroup.entityTypeId.value;
    beneficiary.firstName = primaryContactControls?.firstName?.value ?? null;
    beneficiary.lastName = primaryContactControls?.lastName?.value ?? null;
    beneficiary.organizationName = organizationControls?.organizationName?.value ?? null;
    beneficiary.organizationBusinessNumber = organizationControls?.organizationBusinessNumber?.value ?? null;
    beneficiary.organizationPhoneNumberCountryCode =
      organizationControls?.organizationPhoneNumberCountryCode?.value ?? '';
    beneficiary.organizationPhoneNumber = organizationControls?.organizationPhoneNumber?.value ?? '';
    beneficiary.phoneNumberCountryCode = primaryContactControls?.phoneNumberCountryCode?.value ?? '';
    beneficiary.phoneNumber = primaryContactControls?.phoneNumber?.value ?? '';
    beneficiary.address = Deserialize?.instanceOf(Address, organizationControls?.address?.value) ?? null;
    return beneficiary;
  }

  private static constructEstateBeneficiary(formGroup: AccountTypeBeneficiaryFormGroup): Beneficiary {
    const Deserialize = window?.injector.Deserialize;
    const beneficiary = new Beneficiary();
    const estateControls = formGroup.estate?.controls;
    const contactType = estateControls?.primaryContactType?.value;
    beneficiary.memberBeneficiaryId = formGroup.memberBeneficiaryId.value ?? null;
    beneficiary.typeId = formGroup.typeId.value;
    beneficiary.entityTypeId = formGroup.entityTypeId.value;

    if (contactType === EstatePrimaryContactTypeEnum.Individual) {
      const individualControls = estateControls?.individual?.controls;
      beneficiary.firstName = individualControls?.firstName?.value ?? null;
      beneficiary.lastName = individualControls?.lastName?.value ?? null;
      beneficiary.phoneNumberCountryCode = individualControls?.phoneNumberCountryCode?.value ?? '';
      beneficiary.phoneNumber = individualControls?.phoneNumber?.value ?? '';
    } else {
      const organizationControls = estateControls?.organization?.controls;
      beneficiary.organizationName = organizationControls?.organizationName?.value ?? null;
      beneficiary.organizationPhoneNumberCountryCode =
        organizationControls?.organizationPhoneNumberCountryCode?.value ?? '';
      beneficiary.organizationPhoneNumber = organizationControls?.organizationPhoneNumber?.value ?? '';
      beneficiary.address = Deserialize?.instanceOf(Address, organizationControls?.address?.value) ?? null;
    }
    return beneficiary;
  }

  public static createBeneficiaryFromFormGroup(formGroup: AccountTypeBeneficiaryFormGroup): Beneficiary {
    let beneficiary!: Beneficiary;
    switch (formGroup.entityTypeId.getRawValue()) {
      case BeneficiaryEntityTypeEnum.Individual:
        beneficiary = this.constructIndividualBeneficiary(formGroup);
        break;
      case BeneficiaryEntityTypeEnum.Organization:
        beneficiary = this.constructOrganizationBeneficiary(formGroup);
        break;
      case BeneficiaryEntityTypeEnum.Estate:
        beneficiary = this.constructEstateBeneficiary(formGroup);
        break;
    }

    if (beneficiary.typeId === BeneficiaryTypeEnum.Alternate) {
      beneficiary.alternateSharePercentage = formGroup.alternateSharePercentage?.value ?? 0;
    } else {
      beneficiary.sharePercentage = formGroup.sharePercentage?.value ?? 0;
    }

    return beneficiary;
  }

  public static beneficiarySharesHaveChanged(changes: BeneficiaryShareChanges): boolean {
    return (
      changes.old.some(c => c.changeType !== BeneficiaryShareChangeTypeEnum.Neutral) ||
      changes.new.some(c => c.changeType !== BeneficiaryShareChangeTypeEnum.Neutral)
    );
  }
}
